import { cn } from '@/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * Figma 라이브러리 Badge & Chip (423:184) — State × Style × Size
 *
 * state: default(파랑) · grey · success · warning · error · info · disabled · clear(흰 배경)
 * emphasis: weak(연한 배경 + 진한 글자) · strong(진한 배경 + 흰 글자)
 * size: medium 32(14/16) · small 24(12/16) · tiny 20~22(10/12)
 * 아이콘은 leftIcon · rightIcon으로 넘긴다 (medium 16, small · tiny 14).
 *
 * 레거시: tone(neutral · muted · app · onWhite · primary · success · warning · danger)은
 * 2단계 화면 이전 전까지 예전 모양 그대로 둔다. state를 주면 새 디자인으로 그린다.
 */
const badgeVariants = cva(
  'inline-flex shrink-0 items-center justify-center whitespace-nowrap',
  {
    variants: {
      size: {
        medium: 'rounded-12 p-2 text-button-14-sb',
        small: 'rounded-8 p-1 text-button-12-sb',
        tiny: 'rounded-4 p-1 text-button-10-sb',
      },
      state: {
        default: '',
        grey: '',
        success: '',
        warning: '',
        error: '',
        info: '',
        disabled: '',
        clear: '',
      },
      emphasis: { weak: '', strong: '' },
    },
    compoundVariants: [
      // weak
      {
        emphasis: 'weak',
        state: 'default',
        className: 'bg-brand-50 text-fg-accent',
      },
      {
        emphasis: 'weak',
        state: 'grey',
        className: 'bg-grey-100 text-fg-primary',
      },
      {
        emphasis: 'weak',
        state: 'success',
        className: 'bg-green-50 text-green-600',
      },
      {
        emphasis: 'weak',
        state: 'warning',
        className: 'bg-yellow-50 text-yellow-600',
      },
      { emphasis: 'weak', state: 'error', className: 'bg-red-50 text-red-600' },
      {
        emphasis: 'weak',
        state: 'info',
        className: 'bg-blue-50 text-blue-500',
      },
      {
        emphasis: 'weak',
        state: 'disabled',
        className: 'bg-grey-100 text-fg-light',
      },
      {
        emphasis: 'weak',
        state: 'clear',
        className: 'bg-white/80 text-fg-secondary',
      },
      // strong
      {
        emphasis: 'strong',
        state: 'default',
        className: 'bg-brand-600 text-fg-white',
      },
      {
        emphasis: 'strong',
        state: 'grey',
        className: 'bg-grey-400 text-fg-white',
      },
      {
        emphasis: 'strong',
        state: 'success',
        className: 'bg-green-500 text-fg-white',
      },
      {
        emphasis: 'strong',
        state: 'warning',
        className: 'bg-yellow-500 text-fg-white',
      },
      {
        emphasis: 'strong',
        state: 'error',
        className: 'bg-red-500 text-fg-white',
      },
      {
        emphasis: 'strong',
        state: 'info',
        className: 'bg-[#0095ff] text-fg-white',
      },
      {
        emphasis: 'strong',
        state: 'disabled',
        className: 'bg-grey-200 text-fg-light',
      },
      {
        emphasis: 'strong',
        state: 'clear',
        className: 'border border-grey-200 bg-surface-white text-fg-secondary',
      },
      {
        emphasis: 'strong',
        state: 'clear',
        size: 'medium',
        className: 'border-[1.5px]',
      },
    ],
    defaultVariants: { size: 'small', state: 'default', emphasis: 'weak' },
  },
);

/** 레거시 tone — 11px · Medium · px 8 / py 3 · radius 5 */
const legacyToneClass = {
  neutral: 'bg-muted text-ink-4', // 그룹 개수
  muted: 'bg-muted text-ink-6', // 거래 종료 · 종료·거절
  app: 'bg-muted text-ink-7', // [앱] 배지 (회색 배경 위면 onWhite 사용)
  onWhite: 'bg-surface text-ink-7', // 비활성 버튼 안의 [앱] 배지
  primary: 'bg-primary-soft text-primary', // 의원급
  success: 'bg-success-soft text-success', // 거래 중 · 병원급
  warning: 'bg-warning-soft text-warning', // 확인 중 · 필터링 신청
  danger: 'bg-danger-soft text-danger', // 신청 거절
} as const;

export type BadgeTone = keyof typeof legacyToneClass;

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** @deprecated 2단계 이전 후 state · emphasis로 바꾼다 */
  tone?: BadgeTone | null;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Badge = ({
  className,
  tone,
  state,
  emphasis,
  size,
  leftIcon,
  rightIcon,
  children,
  ...props
}: BadgeProps) => {
  // state 없이 tone만 주면(또는 아무것도 안 주면) 예전 모양 — 기존 호출부 호환
  if (!state) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded-badge px-2 py-[3px] text-[11px] leading-[17px] font-medium whitespace-nowrap',
          legacyToneClass[tone ?? 'neutral'],
          className,
        )}
        {...props}
      >
        {children}
      </span>
    );
  }
  return (
    <span
      className={cn(badgeVariants({ state, emphasis, size }), className)}
      {...props}
    >
      {leftIcon}
      <span className="px-1">{children}</span>
      {rightIcon}
    </span>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants };
